import { getData, postData, cleanQuotes } from '../utils/global';
import { debug, error as logError, warn } from '../utils/console';

/** Keys stored per-tab in sessionStorage — do not cache to localStorage. */
const SESSION_NAV_SETTING_KEYS = new Set<string>([
  'selectedMenu',
  'selectedPage',
  'selectedDatasetId',
  'selectedDate',
  'selectedSourceId',
  'selectedSourceName',
]);

// Define the structure of persistent settings
export interface PersistentSettings {
  'teamshare-theme': 'dark' | 'light' | 'medium';
  'defaultUnits'?: 'knots' | 'meters';
  colorType: string;
  selectedClassName: string;
  selectedDatasetId: number;
  selectedDate: string;
  selectedEvent: string;
  selectedMenu: string;
  selectedPage: string;
  selectedProjectId: number;
  selectedSourceId: number;
  selectedSourceName: string;
  selectedYear: string;
  // Map overlay settings
  mapOverlays?: {
    'bad-air'?: boolean;
    'boundaries'?: boolean;
    'wind-arrows'?: boolean;
    'current-arrows'?: boolean;
    'wind-contours'?: boolean;
    'maneuvers'?: boolean;
  };
  // Chart overlay positions (keyed by overlay/chart object name): persisted so drag position is reused
  overlayPositions?: Record<string, { position: { x: number; y: number }; orientation: string }>;
  // Performance highlights (persistent across sessions)
  performanceHighlights?: string;
  // Performance color preference (persistent across sessions)
  performanceColor?: string;
  // Performance target name (persistent across sessions)
  performanceTarget?: string;
  // Performance target curve color (persistent across sessions)
  performanceTargetColor?: string;
  // Displayed performance baseline, slot 1. Empty string is an explicit "No baseline", which is
  // sticky — it must be distinguishable from the key being absent (never chosen).
  performanceBaselineId?: string;
  performanceBaselineName?: string;
  // Which CONFIG within that baseline is drawn (a baseline holds one fit per configuration).
  performanceBaselineConfig?: string;
  performanceBaselineColor?: string;
  // Slot 2, the second comparison baseline. Suffixed so slot 1's keys stay backwards compatible.
  performanceBaseline2Id?: string;
  performanceBaseline2Name?: string;
  performanceBaseline2Config?: string;
  performanceBaseline2Color?: string;
  // Displayed maneuver baseline. Separate keys from the performance slots: the two pickers list
  // different baselines (kind='maneuver' vs 'straight_line'), so a shared key would restore an id
  // the other page cannot draw. Same empty-string-means-explicitly-off rule.
  maneuverBaselineId?: string;
  maneuverBaselineName?: string;
  maneuverBaselineConfig?: string;
  maneuverBaselineColor?: string;
  // Fleet performance sources (persistent across sessions). The SHARED source selection: every
  // settings menu writes it and the report pages filter by it.
  fleetPerformanceSources?: string[];
  /**
   * The live map's OWN source selection (boat toggles in the live map settings panel). A separate
   * flat key on purpose: sharing fleetPerformanceSources meant picking boats to watch live
   * re-selected them on every report page, and a report page's selection stomped the map's.
   * Flat, not nested, because saveSettings merges shallowly at the top level.
   */
  liveMapSources?: string[];
  // Maneuver filters (persistent across sessions)
  maneuverFilters?: {
    grades?: number[];
    states?: string[];
    races?: (number | string)[];
    legs?: number[];
    trainingRacing?: 'TRAINING' | 'RACING' | null;
    tackEntry?: 'PORT' | 'STBD' | null;
  };
  /**
   * Maneuver STATE filter, remembered per maneuver type (TACK / GYBE / ROUNDUP / BEARAWAY /
   * TAKEOFF). Separate from maneuverFilters.states above, which is the flat pre-per-type value and
   * is now only read as a first-visit seed: the state vocabularies are disjoint per type, so one
   * shared list means a type switch either blanks the table or throws the selection away. Written
   * and read only through utils/maneuverStatePersistence, which does the read-modify-write this
   * shallow top-level merge requires.
   */
  maneuverStatesByEventType?: Record<string, string[]>;
  // Performance filters (persistent across sessions)
  performanceFilters?: {
    grades?: string;
    year?: string;
    event?: string;
    config?: string;
    state?: string;
    trainingRacing?: 'TRAINING' | 'RACING' | null;
    tack?: 'PORT' | 'STBD' | null;
  };
  // Performance history date range (persistent across sessions)
  performanceHistoryDateRange?: {
    startDate?: string;
    endDate?: string;
  };
  // Fleet Summary Tables filter memory (grades, states, timeline toggle)
  summaryTablesFilters?: {
    grades?: number[];
    states?: string[];
    trainingRacing?: 'TRAINING' | 'RACING' | null;
    showTimeline?: boolean;
  };
  // Last selected menu for each mode (dataset vs day)
  lastMenu_dataset?: string;
  lastMenu_day?: string;
  // Timeline visibility preference
  showTimeline?: boolean;
  /**
   * Live Fleet Summary: pivot the tables (metrics as rows, sources as columns).
   * Deliberately a flat key, not a field inside summaryTablesFilters — saveSettings merges
   * shallowly at the top level, so the project Fleet Summary Tables page writing
   * summaryTablesFilters would wipe a nested value. Give any other page its own flat key.
   */
  liveSummaryTablesPivoted?: boolean;
  /** Race Summary: pivot the tables. Flat key, same reasoning as above. */
  raceSummaryTablesPivoted?: boolean;
  /** Training Summary: pivot the tables. Flat key, same reasoning as above. */
  trainingSummaryTablesPivoted?: boolean;
  /**
   * Live Maneuvers / Live Performance display scope in minutes (0 = All, default 60). Flat keys,
   * same shallow-merge reasoning as above. This is deliberately separate from the live map's
   * playbackStore timeWindow, which clamps to 30 minutes in live mode.
   */
  liveManeuversTimeWindowMinutes?: number;
  livePerformanceTimeWindowMinutes?: number;
  /**
   * Live Fleet Summary debrief display options: which boat the strip tables compare against, and
   * whether to draw +/-1 sigma whiskers. Flat keys, same shallow-merge reasoning as above.
   */
  liveDebriefHighlightBoat?: string;
  liveDebriefWhiskers?: boolean;
  /**
   * Home boat for actionable insight cards (vs leaders). Flat key — same shallow-merge reasoning
   * as liveDebriefHighlightBoat. Empty string = unset (panel prompts to pick).
   */
  homeBoatName?: string;
  /** Live Fleet Summary follow window in minutes (0 = whole session, default 60). Flat key. */
  liveDebriefTimeWindowMinutes?: number;
  /** Performance scatter Display Options → Fits: draw fit lines on every chart, not only the expanded one. Flat key. */
  showScatterFits?: boolean;
  /** Performance scatter: small faded points + thick fit/target lines (fits only when Fits is ON). Flat key. */
  highlightScatterLines?: boolean;
  // Channel picker data source preference
  channelPickerDataSource?: 'FILE' | 'INFLUX';
}

// Default settings values
const DEFAULT_SETTINGS: PersistentSettings = {
  'teamshare-theme': 'medium',
  colorType: 'DEFAULT',
  selectedClassName: '',
  selectedDatasetId: 0,
  selectedDate: '',
  selectedEvent: '',
  selectedMenu: 'Datasets',
  selectedPage: '',
  selectedProjectId: 0,
  selectedSourceId: 0,
  selectedSourceName: '',
  selectedYear: '',
  showTimeline: true,
  showScatterFits: false,
  highlightScatterLines: false,
  channelPickerDataSource: 'FILE'
};

class PersistentSettingsService {
  /**
   * Debounced writes, one entry per user+class+project, with the partial settings ACCUMULATED.
   *
   * This used to be a single shared timer that the next call simply cleared, so two saves inside the
   * 500 ms window did not coalesce — the earlier one was dropped outright, keys and all, even when
   * the two wrote different top-level keys (and even when they were for different projects). The
   * maneuver per-event-type STATE map racing ManeuverSettings' Apply is exactly that shape. Merging
   * the queued partial keeps both; a later value for the same key still wins.
   */
  private pendingSaves = new Map<string, {
    settings: Partial<PersistentSettings>;
    timeout: NodeJS.Timeout;
  }>();
  /** Coalesce parallel loadSettings(userId, className, projectId) calls into one HTTP request. */
  private loadSettingsInflight = new Map<string, Promise<PersistentSettings | null>>();

  /**
   * Cache settings locally in both localStorage and HuniDB (per-class database)
   */
  private async cacheToLocalStores(
    userId: string,
    className: string,
    projectId: number,
    settings: Partial<PersistentSettings>
  ): Promise<void> {
    try {
      // LocalStorage cache (fast, global)
      this.applySettingsToLocalStorage(settings);
    } catch (err) {
      warn('[PersistentSettings] Failed to cache settings to localStorage:', err);
    }

    // HuniDB cache (per-class, structured)
    try {
      // Dynamic import to avoid heavy coupling / circular deps
      const { huniDBStore } = await import('../store/huniDBStore');
      const key = `user_settings_${userId}_${projectId}`;
      await huniDBStore.storeObject(className, key, settings);
      debug('[PersistentSettings] Cached settings to HuniDB', { key, className, projectId });
    } catch (err) {
      // HuniDB is an optimization; don't fail if it's not available
      warn('[PersistentSettings] Failed to cache settings to HuniDB (non-fatal):', err);
    }
  }

  /**
   * Load settings from HuniDB cache (per-class objects table)
   */
  private async loadFromHuniDB(
    userId: string,
    className: string,
    projectId: number
  ): Promise<PersistentSettings | null> {
    try {
      const { huniDBStore } = await import('../store/huniDBStore');
      const key = `user_settings_${userId}_${projectId}`;
      const cached = await huniDBStore.getObject(className, key);

      if (cached && typeof cached === 'object') {
        debug('[PersistentSettings] Loaded settings from HuniDB cache', { key, className, projectId });
        return { ...DEFAULT_SETTINGS, ...(cached as Partial<PersistentSettings>) };
      }

      debug('[PersistentSettings] No settings found in HuniDB cache', { key, className, projectId });
      return null;
    } catch (err) {
      // Log error and fall back to API
      debug('[PersistentSettings] Failed to load settings from HuniDB (falling back to API):', err);
      return null;
    }
  }

  /**
   * Load settings from API, fallback to localStorage if API fails
   * If no settings exist, return null to indicate initialization needed
   * Includes retry logic for network failures
   */
  async loadSettings(userId: string, className: string, projectId: number, retries: number = 3): Promise<PersistentSettings | null> {
    const key = `${userId}\0${className}\0${String(projectId)}`;
    const inflight = this.loadSettingsInflight.get(key);
    if (inflight) {
      debug('[PersistentSettings] Coalescing in-flight loadSettings', { userId, className, projectId });
      return inflight;
    }
    const promise = this.loadSettingsOnce(userId, className, projectId, retries).finally(() => {
      this.loadSettingsInflight.delete(key);
    });
    this.loadSettingsInflight.set(key, promise);
    return promise;
  }

  private async loadSettingsOnce(userId: string, className: string, projectId: number, retries: number = 3): Promise<PersistentSettings | null> {
    const url = `/api/users/settings?user_id=${encodeURIComponent(userId)}`;
    

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        debug(`[PersistentSettings] Loading settings from API (attempt ${attempt}/${retries})`, { userId, className, projectId });
        
        const response = await getData(url);
        
        debug('[PersistentSettings] API response:', { 
          success: response.success, 
          status: response.status, 
          hasData: !!response.data,
          dataType: typeof response.data,
          dataValue: response.data,
          message: response.message,
          error: response.error
        });
        
        // Check if response indicates no settings found
        // The API returns success: true with message but no data field when settings don't exist
        if (!response.success || response.data === null || response.data === undefined) {
          // Check if it's a 404 (not found) vs an actual error
          if (response.status === 404 || (response.message && response.message.includes('No settings found'))) {
            debug('[PersistentSettings] No settings found in API (404), returning null for initialization');
          } else if (response.success && !response.data && response.message) {
            // API returned success but no data - this means no settings exist
            debug('[PersistentSettings] API returned success but no data field, returning null for initialization');
          } else {
            debug('[PersistentSettings] Error loading settings from API:', response.message || response.error);
          }
          return null; // Indicate no settings exist, need to initialize
        }
        
        // Settings found - parse them
        debug('[PersistentSettings] Settings loaded from API', response.data);
        
        // Parse the JSON if it's a string, otherwise use it directly
        let parsedSettings: Partial<PersistentSettings>;
        if (typeof response.data === 'string') {
          try {
            parsedSettings = JSON.parse(response.data);
          } catch (parseError) {
            warn('[PersistentSettings] Failed to parse settings JSON string:', parseError);
            return null;
          }
        } else {
          // response.data is already an object (JSONB from PostgreSQL)
          parsedSettings = response.data;
        }
        
        // Ensure the source-selection arrays are properly parsed as arrays
        for (const key of ['fleetPerformanceSources', 'liveMapSources'] as const) {
          const value = parsedSettings[key];
          if (!value) continue;
          if (typeof value === 'string') {
            try {
              parsedSettings[key] = JSON.parse(value);
            } catch (parseError) {
              warn(`[PersistentSettings] Failed to parse ${key} as array:`, parseError);
              parsedSettings[key] = undefined;
            }
          }
          if (parsedSettings[key] !== undefined && !Array.isArray(parsedSettings[key])) {
            warn(`[PersistentSettings] ${key} is not an array, clearing it`);
            parsedSettings[key] = undefined;
          }
        }
        
        // Clean string values that may have quotes
        const cleanedSettings = this.cleanSettingsStrings(parsedSettings);
        
        const merged = { ...DEFAULT_SETTINGS, ...cleanedSettings };

        // Cache API-derived persistent state (session nav fields are excluded in applySettingsToLocalStorage)
        void this.cacheToLocalStores(userId, className, projectId, merged);

        return merged;
      } catch (error) {
        const isLastAttempt = attempt === retries;
        const errorMessage = error instanceof Error ? error.message : String(error);
        
        if (isLastAttempt) {
          warn(`[PersistentSettings] Failed to load settings from API after ${retries} attempts, falling back to local caches:`, errorMessage);

          // Prefer HuniDB cache, then localStorage
          const huniCached = await this.loadFromHuniDB(userId, className, projectId);
          if (huniCached) {
            return huniCached;
          }

          return this.loadFromLocalStorage();
        } else {
          // Wait before retrying (exponential backoff)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          debug(`[PersistentSettings] API load failed (attempt ${attempt}/${retries}), retrying in ${delay}ms:`, errorMessage);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // Should never reach here, but TypeScript needs it
    return this.loadFromLocalStorage();
  }

  /**
   * Save settings to API with debouncing and retry logic
   * Merges new settings with existing settings to avoid overwriting other settings
   */
  async saveSettings(userId: string, className: string, projectId: number, settings: Partial<PersistentSettings>, retries: number = 3): Promise<void> {
    const pendingKey = `${userId}|${className}|${projectId}`;
    const pending = this.pendingSaves.get(pendingKey);
    if (pending) {
      clearTimeout(pending.timeout);
    }
    // Accumulate rather than replace — see pendingSaves.
    const queuedSettings: Partial<PersistentSettings> = { ...(pending?.settings ?? {}), ...settings };

    // Debounce saves by 500ms
    const timeout = setTimeout(async () => {
      this.pendingSaves.delete(pendingKey);
      // Load existing settings from cache first (fast, local) to merge with new settings
      // Prefer HuniDB cache, then localStorage, only fallback to API if both fail
      let existingSettings: PersistentSettings | null = null;
      
      try {
        // Try HuniDB cache first (fastest, per-class)
        existingSettings = await this.loadFromHuniDB(userId, className, projectId);
        
        // If not in HuniDB, try localStorage (also fast)
        if (!existingSettings) {
          existingSettings = this.loadFromLocalStorage();
          debug('[PersistentSettings] Using localStorage cache for merge');
        } else {
          debug('[PersistentSettings] Using HuniDB cache for merge');
        }
      } catch (error) {
        debug('[PersistentSettings] Could not load cached settings for merge, will use defaults:', error);
        // Fallback to localStorage if HuniDB fails
        try {
          existingSettings = this.loadFromLocalStorage();
        } catch (localError) {
          debug('[PersistentSettings] Could not load from localStorage either, will create new');
        }
      }
      
      // Merge existing settings with new settings (new settings override existing)
      const mergedSettings: Partial<PersistentSettings> = existingSettings
        ? { ...existingSettings, ...queuedSettings }
        : { ...DEFAULT_SETTINGS, ...queuedSettings };

      // Immediately cache merged settings locally (localStorage + HuniDB) for faster access
      // even if API save fails or is delayed.
      void this.cacheToLocalStores(userId, className, projectId, mergedSettings);
      
      const payload = {
        user_id: userId,
        json: JSON.stringify(mergedSettings)
      };

      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          debug(`[PersistentSettings] Saving settings to API (attempt ${attempt}/${retries})`, { userId, className, projectId, newSettings: queuedSettings, mergedSettings: mergedSettings });
          
          const response = await postData('/api/users/settings', payload);
          
          if (response.success) {
            debug('[PersistentSettings] Settings saved successfully to API');
            return; // Success, exit retry loop
          } else {
            const isLastAttempt = attempt === retries;
            if (isLastAttempt) {
              warn(`[PersistentSettings] Failed to save settings to API after ${retries} attempts:`, response.message);
            } else {
              const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
              debug(`[PersistentSettings] API save failed (attempt ${attempt}/${retries}), retrying in ${delay}ms:`, response.message);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        } catch (error) {
          const isLastAttempt = attempt === retries;
          const errorMessage = error instanceof Error ? error.message : String(error);
          
          if (isLastAttempt) {
            logError(`[PersistentSettings] Error saving settings to API after ${retries} attempts:`, errorMessage);
          } else {
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            debug(`[PersistentSettings] API save error (attempt ${attempt}/${retries}), retrying in ${delay}ms:`, errorMessage);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
    }, 500);
    this.pendingSaves.set(pendingKey, { settings: queuedSettings, timeout });
  }

  /**
   * Clean string values in settings to remove quotes
   */
  private cleanSettingsStrings(settings: Partial<PersistentSettings>): Partial<PersistentSettings> {
    const cleaned: Partial<PersistentSettings> = { ...settings };
    
    // Clean string fields
    if (cleaned.selectedClassName && typeof cleaned.selectedClassName === 'string') {
      cleaned.selectedClassName = cleanQuotes(cleaned.selectedClassName);
    }
    if (cleaned.selectedDate && typeof cleaned.selectedDate === 'string') {
      cleaned.selectedDate = cleanQuotes(cleaned.selectedDate);
    }
    if (cleaned.selectedEvent && typeof cleaned.selectedEvent === 'string') {
      cleaned.selectedEvent = cleanQuotes(cleaned.selectedEvent);
    }
    if (cleaned.selectedMenu && typeof cleaned.selectedMenu === 'string') {
      cleaned.selectedMenu = cleanQuotes(cleaned.selectedMenu);
    }
    if (cleaned.selectedPage && typeof cleaned.selectedPage === 'string') {
      cleaned.selectedPage = cleanQuotes(cleaned.selectedPage);
    }
    if (cleaned.selectedSourceName && typeof cleaned.selectedSourceName === 'string') {
      cleaned.selectedSourceName = cleanQuotes(cleaned.selectedSourceName);
    }
    if (cleaned.selectedYear && typeof cleaned.selectedYear === 'string') {
      cleaned.selectedYear = cleanQuotes(cleaned.selectedYear);
    }
    if (cleaned.performanceColor && typeof cleaned.performanceColor === 'string') {
      cleaned.performanceColor = cleanQuotes(cleaned.performanceColor);
    }
    if (cleaned.performanceHighlights && typeof cleaned.performanceHighlights === 'string') {
      cleaned.performanceHighlights = cleanQuotes(cleaned.performanceHighlights);
    }
    if (cleaned.performanceTarget && typeof cleaned.performanceTarget === 'string') {
      cleaned.performanceTarget = cleanQuotes(cleaned.performanceTarget);
    }
    if (cleaned.performanceTargetColor && typeof cleaned.performanceTargetColor === 'string') {
      cleaned.performanceTargetColor = cleanQuotes(cleaned.performanceTargetColor);
    }
    if (cleaned.performanceBaselineId && typeof cleaned.performanceBaselineId === 'string') {
      cleaned.performanceBaselineId = cleanQuotes(cleaned.performanceBaselineId);
    }
    if (cleaned.performanceBaselineName && typeof cleaned.performanceBaselineName === 'string') {
      cleaned.performanceBaselineName = cleanQuotes(cleaned.performanceBaselineName);
    }
    if (cleaned.performanceBaselineConfig && typeof cleaned.performanceBaselineConfig === 'string') {
      cleaned.performanceBaselineConfig = cleanQuotes(cleaned.performanceBaselineConfig);
    }
    if (cleaned.performanceBaselineColor && typeof cleaned.performanceBaselineColor === 'string') {
      cleaned.performanceBaselineColor = cleanQuotes(cleaned.performanceBaselineColor);
    }

    // Clean performanceFilters object
    if (cleaned.performanceFilters) {
      const filters = { ...cleaned.performanceFilters };
      if (filters.grades && typeof filters.grades === 'string') {
        filters.grades = cleanQuotes(filters.grades);
      }
      if (filters.year && typeof filters.year === 'string') {
        filters.year = cleanQuotes(filters.year);
      }
      if (filters.event && typeof filters.event === 'string') {
        filters.event = cleanQuotes(filters.event);
      }
      if (filters.config && typeof filters.config === 'string') {
        filters.config = cleanQuotes(filters.config);
      }
      if (filters.state && typeof filters.state === 'string') {
        filters.state = cleanQuotes(filters.state);
      }
      cleaned.performanceFilters = filters;
    }
    
    // Clean performanceHistoryDateRange object
    if (cleaned.performanceHistoryDateRange) {
      const dateRange = { ...cleaned.performanceHistoryDateRange };
      if (dateRange.startDate && typeof dateRange.startDate === 'string') {
        dateRange.startDate = cleanQuotes(dateRange.startDate);
      }
      if (dateRange.endDate && typeof dateRange.endDate === 'string') {
        dateRange.endDate = cleanQuotes(dateRange.endDate);
      }
      cleaned.performanceHistoryDateRange = dateRange;
    }
    
    return cleaned;
  }

  /**
   * Load settings from localStorage as fallback
   */
  private loadFromLocalStorage(): PersistentSettings {
    debug('[PersistentSettings] Loading settings from localStorage');
    
    const settings: Partial<PersistentSettings> = {};
    
    // Load each setting from localStorage
    const keys: (keyof PersistentSettings)[] = [
      'teamshare-theme', 'defaultUnits', 'colorType', 'selectedClassName',
      'selectedEvent', 'selectedProjectId', 'selectedYear'
    ];
    
    // Also load defaultUnits from 'teamshare-units' localStorage key for backward compatibility
    try {
      const unitsValue = localStorage.getItem('teamshare-units');
      if (unitsValue === 'knots' || unitsValue === 'meters') {
        settings['defaultUnits'] = unitsValue;
      }
    } catch (error) {
      // Ignore error
    }

    keys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        if (value !== null) {
          // Parse JSON for complex values, clean quotes from strings
          if (key === 'selectedDatasetId' || key === 'selectedProjectId' || key === 'selectedSourceId') {
            settings[key] = parseInt(value) || 0;
          } else {
            // Clean quotes from string values
            settings[key] = cleanQuotes(value) as any;
          }
        }
      } catch (error) {
        warn(`[PersistentSettings] Error loading ${key} from localStorage:`, error);
      }
    });

    // Load overlayPositions from localStorage (object keyed by overlay name)
    try {
      const overlayPositionsValue = localStorage.getItem('overlayPositions');
      if (overlayPositionsValue !== null) {
        try {
          const parsed = JSON.parse(overlayPositionsValue);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            settings.overlayPositions = parsed;
          }
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    }

    // Load the source-selection arrays from localStorage (arrays need JSON parsing)
    for (const key of ['fleetPerformanceSources', 'liveMapSources'] as const) {
      try {
        const rawValue = localStorage.getItem(key);
        if (rawValue === null) continue;
        try {
          const parsed = JSON.parse(rawValue);
          if (Array.isArray(parsed)) {
            settings[key] = parsed;
          }
        } catch (parseError) {
          // If parsing fails, try to treat it as a single string (backward compatibility)
          warn(`[PersistentSettings] Failed to parse ${key}, treating as single value`);
        }
      } catch (error) {
        warn(`[PersistentSettings] Error loading ${key} from localStorage:`, error);
      }
    }

    const result = { ...DEFAULT_SETTINGS, ...settings };
    // Clean string values that may have quotes
    return this.cleanSettingsStrings(result) as PersistentSettings;
  }

  /**
   * Get current settings from localStorage (for immediate access)
   */
  getCurrentSettings(): PersistentSettings {
    return this.loadFromLocalStorage();
  }

  /**
   * Apply settings to localStorage (for caching)
   */
  applySettingsToLocalStorage(settings: Partial<PersistentSettings>): void {
    debug('[PersistentSettings] Applying settings to localStorage', settings);
    
    Object.entries(settings).forEach(([key, value]) => {
      if (SESSION_NAV_SETTING_KEYS.has(key)) {
        return;
      }
      if (value !== undefined && value !== null) {
        // Properly serialize objects and arrays to JSON, keep primitives as strings
        if (typeof value === 'object') {
          try {
            localStorage.setItem(key, JSON.stringify(value));
          } catch (error) {
            warn(`[PersistentSettings] Failed to serialize ${key} to localStorage:`, error);
          }
        } else {
          localStorage.setItem(key, String(value));
        }
      }
    });
  }
}

// Export singleton instance
export const persistentSettingsService = new PersistentSettingsService();
